import { Injectable } from '@angular/core';
import {
  AuthService,
  ProductService,
  STOCK_STATUS,
  Availability,
  AvailabilityHelperService,
  AvailabilityDisplay,
  Product,
  GungAnonymousConfigService,
  MetadataService
} from 'gung-standard';
import { DateUtilService } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import { filter, first } from 'rxjs';
import { Observable, of } from 'rxjs';
import { environment } from './../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TyloAvailabilityHelperService extends AvailabilityHelperService {
  activeGroups: { [s: string]: boolean };

  isHelo = environment.projectName === 'helo';

  currentProduct: Product;

  constructor(
    protected authService: AuthService,
    protected translateService: TranslateService,
    protected productService: ProductService,
    protected dateUtilService: DateUtilService,
    protected gungAnonymousConfigService: GungAnonymousConfigService,
    protected metadataService: MetadataService
  ) {
    super(authService, translateService, dateUtilService, gungAnonymousConfigService, metadataService);
    this.authService
      .getCurrentUser()
      .pipe(
        filter(user => !!user),
        first()
      )
      .subscribe(user => {
        this.activeGroups = user.activeGroups;
      });
  }

  protected getValueFromStatus(status: STOCK_STATUS, isSales: boolean, availability: Availability): string {
    if (!availability) {
      return '';
    }
    if (this.isHelo) {
      return this.heloGetValueFromStatus(status, isSales, availability);
    }
    let value: string;

    // user and sales will be the same info about availability
    if (status === STOCK_STATUS.IN_STOCK) {
      value = `${this.translateService.instant('IN_STOCK')} (${availability.currentAvailability})`;
    } else if (status === STOCK_STATUS.INCOMING) {
      const firstDate = Object.keys(availability.extra.availabilities).filter(key => {
        return availability.extra.availabilities[key] > 0;
      })[0];
      value = this.translateService.instant('EXPECTED_DATE', { date: firstDate });
    }

    if (status === STOCK_STATUS.OUT_OF_STOCK) {
      value = this.translateService.instant('OUT_OF_STOCK');
    }

    return value;
  }

  public getAvailabilityDisplay(availability: Availability, product: Product = null): Observable<AvailabilityDisplay> {
    if (this.activeGroups.NOPRICE) {
      return of({ value: '', class: { 'd-none': true } });
    }
    this.currentProduct = product;
    return super.getAvailabilityDisplay(availability, product);
  }

  heloGetValueFromStatus(status: STOCK_STATUS, isSales: boolean, availability: Availability) {
    let value = '';


    const leadtimeDate = Object.keys(availability.extra.availabilities)?.find(key => availability.extra.availabilities[key] >= 100000);
    const leadtimeInDays = this.dateUtilService.getDifference(this.dateUtilService.parseDate(leadtimeDate, 'yyMMdd'), new Date(), 'days');

    if (status === STOCK_STATUS.IN_STOCK) {
      value = `<span class="available">${this.translateService.instant('IN_STOCK')} (${availability.currentAvailability
        })</span>`;

      if (leadtimeDate && this.currentProduct?.extra?.ar?.itemstatuscode !== 8) {
        if (leadtimeInDays > 0) {
          value += `<br class="d-md-block d-lg-none"> <span class="expected d-md-block d-lg-none">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays })}</span>
          <br class="d-none d-md-none d-lg-block"> <span class="expected position-absolute d-none d-md-none d-lg-block">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays })}</span>`;
        } else if (leadtimeInDays == 0) {
          value += `<br class="d-md-block d-lg-none"> <span class="expected d-md-block d-lg-none">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays + 1 })}</span>
          <br class="d-none d-md-none d-lg-block"> <span class="expected position-absolute d-none d-md-none d-lg-block">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays + 1 })}</span>`;
        }
      }
    } else if (status === STOCK_STATUS.INCOMING) {
      value = this.translateService.instant('OUT_OF_STOCK');
      if (leadtimeDate && this.currentProduct?.extra?.ar?.itemstatuscode !== 8) {
        if (leadtimeInDays > 0) {
          value += `<br class="d-md-block d-lg-none"> <span class="expected d-md-block d-lg-none">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays })}</span>
          <br class="d-none d-md-none d-lg-block"> <span class="expected position-absolute d-none d-md-none d-lg-block">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays })}</span>`;
        } else if (leadtimeInDays == 0) {
          value += `<br class="d-md-block d-lg-none"> <span class="expected d-md-block d-lg-none">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays + 1 })}</span>
          <br class="d-none d-md-none d-lg-block"> <span class="expected position-absolute d-none d-md-none d-lg-block">${this.translateService.instant('ESTIMATED_PRODUCTION_TIME', { leadtime: leadtimeInDays + 1 })}</span>`;

        }
      }
    }

    if (status === STOCK_STATUS.OUT_OF_STOCK) {
      value = this.translateService.instant('OUT_OF_STOCK');
    }
    return value;
  }
}
